import { forwardRef, PropsWithoutRef, Suspense, useImperativeHandle } from 'react';
import { createZodFormResolver, lt, showSuccessAlert, useFormControls } from '@mews/framework';
import { EnumSelectField } from '@mews-commander/form';
import { Loader } from '@mews-commander/ui';
import { PaymentCardFormat } from '@mews-commander/data-access';
import { Stack } from '@optimus-web/core';
import { PaymentFormProps } from '../types';
import { useDefaultFormContext } from '../useDefaultFormValues';
import { useTokenizeCard, useInvalidatePaymentMethodsTableRecordsQuery } from '../../../../queries';
import { useGetSupportedGatewayCardTypesSuspense } from '../queries';
import { createTokenizeCardSchema, TokenizeCardFormField, TokenizeCardFormValues } from './schema';
import { TokenizeCardFormFields } from './TokenizeCardFormFields';

export const TokenizeCardForm = forwardRef(
    ({ accountId, onSuccess, onSubmitting, onSubmitted }: PropsWithoutRef<PaymentFormProps>, ref) => {
        const invalidatePaymentMethods = useInvalidatePaymentMethodsTableRecordsQuery();

        const {
            data: { supportedPaymentCardTypes },
        } = useGetSupportedGatewayCardTypesSuspense();

        const tokenizeCard = useTokenizeCard({ accountId });

        const { defaultFormValues } = useDefaultFormContext();
        const resolver = createZodFormResolver(createTokenizeCardSchema(supportedPaymentCardTypes));

        const { renderFormContent, submit, hasDirtyFields } = useFormControls<TokenizeCardFormValues>({
            onSubmit: async (formValues) => {
                try {
                    onSubmitting?.();

                    const { lastDigits } = await tokenizeCard(formValues);

                    invalidatePaymentMethods({ customerId: accountId });

                    showSuccessAlert({
                        description: lt('CardCreatedSuccessMessage', { cardLastDigits: lastDigits ?? '' }),
                    });

                    onSuccess?.();
                } finally {
                    onSubmitted?.();
                }
            },
            defaultValues: {
                ...defaultFormValues,
                [TokenizeCardFormField.PaymentCardFormat]: PaymentCardFormat.Physical,
            },
            resolver,
        });

        useImperativeHandle(
            ref,
            () => ({
                submit,
                hasDirtyFields,
            }),
            [submit, hasDirtyFields]
        );

        return renderFormContent(
            <Suspense fallback={<Loader />}>
                <Stack spacing="300" vertical>
                    <TokenizeCardFormFields />
                    <EnumSelectField
                        required
                        enumClass={PaymentCardFormat}
                        enumPrefix="PaymentCardFormat"
                        label={lt('Format')}
                        name={TokenizeCardFormField.PaymentCardFormat}
                    />
                </Stack>
            </Suspense>
        );
    }
);
